import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Copy, ExternalLink, MessageSquare, Pause, Play } from 'lucide-react';
import { widgetsApi } from '../api/widgetsApi';
import { workspaceApi } from '../api/workspaceApi';
import { showErrorToast, showSuccessToast } from '../lib/toast';
import { cn } from '../lib/utils';

const formatNumber = value => new Intl.NumberFormat().format(Number(value || 0));

const formatWhen = value => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

const Stat = ({ label, value }) => (
  <div className="rounded-2xl border border-gray-100 bg-white px-4 py-3 shadow-sm dark:border-gray-800 dark:bg-gray-900">
    <p className="text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-gray-400">{label}</p>
    <p className="mt-1 text-lg font-black tabular-nums text-gray-950 dark:text-white">{value}</p>
  </div>
);

const WidgetCard = ({ widget, onChange, onRemove }) => {
  const [originsText, setOriginsText] = useState((widget.allowed_origins || []).join('\n'));
  const [busy, setBusy] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setOriginsText((widget.allowed_origins || []).join('\n'));
  }, [widget.allowed_origins]);

  const save = async (patch) => {
    setBusy(true);
    try {
      const updated = await workspaceApi.updateEmbed(widget.workspace_id, patch);
      onChange({
        ...widget,
        ...updated,
        allowed_origins: updated.allowed_origins || [],
      });
      showSuccessToast('Widget updated.');
    } catch (err) {
      console.error('Failed to update widget:', err);
      showErrorToast('Could not update this widget.');
    } finally {
      setBusy(false);
    }
  };

  const saveOrigins = () => {
    const allowed_origins = originsText.split('\n').map(line => line.trim()).filter(Boolean);
    save({ allowed_origins, enabled: widget.enabled !== false });
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await workspaceApi.revokeEmbed(widget.workspace_id);
      onRemove(widget.workspace_id);
      showSuccessToast('Widget revoked.');
    } catch (err) {
      console.error('Failed to revoke widget:', err);
      showErrorToast('Could not revoke this widget.');
      setBusy(false);
    }
  };

  const copySnippet = async () => {
    try {
      await navigator.clipboard.writeText(widget.snippet);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy snippet:', err);
      showErrorToast('Could not copy the snippet.');
    }
  };

  const usage = widget.usage || {};
  const lastQuestion = usage.last_message_at ? formatWhen(usage.last_message_at) : null;

  return (
    <article className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm dark:border-gray-800 dark:bg-gray-900">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="truncate text-lg font-black text-gray-950 dark:text-white">
              {widget.workspace_name}
            </h2>
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-widest',
                widget.enabled
                  ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300'
                  : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
              )}
            >
              {widget.enabled ? 'Live' : 'Paused'}
            </span>
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {lastQuestion ? `Last question ${lastQuestion}` : 'No questions yet'}
          </p>
        </div>
        <Link
          to={`/workspace/${widget.workspace_id}`}
          className="text-xs font-bold uppercase tracking-widest text-emerald-700 hover:text-emerald-800 dark:text-emerald-400"
        >
          Open workspace
        </Link>
      </div>

      <dl className="mt-4 grid grid-cols-3 gap-2">
        <div className="rounded-2xl bg-gray-50 px-3 py-2 dark:bg-gray-950">
          <dt className="text-[10px] font-black uppercase tracking-widest text-gray-400">This period</dt>
          <dd className="mt-1 text-sm font-black tabular-nums text-gray-900 dark:text-white">
            {formatNumber(usage.messages_this_period)}
          </dd>
        </div>
        <div className="rounded-2xl bg-gray-50 px-3 py-2 dark:bg-gray-950">
          <dt className="text-[10px] font-black uppercase tracking-widest text-gray-400">All time</dt>
          <dd className="mt-1 text-sm font-black tabular-nums text-gray-900 dark:text-white">
            {formatNumber(usage.messages_total)}
          </dd>
        </div>
        <div className="rounded-2xl bg-gray-50 px-3 py-2 dark:bg-gray-950">
          <dt className="text-[10px] font-black uppercase tracking-widest text-gray-400">Chats</dt>
          <dd className="mt-1 text-sm font-black tabular-nums text-gray-900 dark:text-white">
            {formatNumber(usage.conversations)}
          </dd>
        </div>
      </dl>

      <label className="mt-4 block text-[10px] font-black uppercase tracking-widest text-gray-500">
        Snippet
        <textarea
          readOnly
          value={widget.snippet}
          rows={11}
          className="mt-1 w-full resize-none rounded-xl border border-gray-200 bg-gray-50 p-3 font-mono text-xs text-gray-800 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
        />
      </label>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        Open Preview to try each layout and copy install steps for HTML, React, Next.js, Vue, or WordPress. You can still set <span className="font-mono">data-color</span>, <span className="font-mono">data-message</span>, and <span className="font-mono">data-question</span> on the script tag.
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={copySnippet}
          className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? 'Copied' : 'Copy snippet'}
        </button>
        <a
          href={`/embed?key=${encodeURIComponent(widget.public_key)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
        >
          <ExternalLink size={14} />
          Preview
        </a>
      </div>

      <label className="mt-4 block text-[10px] font-black uppercase tracking-widest text-gray-500">
        Allowed websites
        <textarea
          value={originsText}
          onChange={event => setOriginsText(event.target.value)}
          rows={3}
          placeholder={'https://example.com\nhttps://www.example.com'}
          className="mt-1 w-full rounded-xl border border-gray-200 p-3 font-mono text-xs text-gray-800 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
        />
      </label>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        One origin per line, including https. The widget only runs on the sites you list. The host page has to send a referrer so the widget can tell which site it is on.
      </p>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => save({ enabled: !widget.enabled })}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-gray-900 disabled:opacity-50 dark:text-gray-300"
        >
          {widget.enabled ? <Pause size={14} /> : <Play size={14} />}
          {widget.enabled ? 'Pause' : 'Resume'}
        </button>
        <div className="flex flex-wrap gap-2">
          {confirmRevoke ? (
            <>
              <button
                type="button"
                disabled={busy}
                onClick={() => setConfirmRevoke(false)}
                className="rounded-lg px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={revoke}
                className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                Revoke key
              </button>
            </>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={() => setConfirmRevoke(true)}
              className="rounded-lg px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"
            >
              Revoke
            </button>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={saveOrigins}
            className="rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-gray-800 disabled:opacity-50 dark:bg-white dark:text-gray-900"
          >
            Save sites
          </button>
        </div>
      </div>
    </article>
  );
};

const WidgetsPage = () => {
  const [page, setPage] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'manteo.ai | Widgets';
  }, []);

  useEffect(() => {
    let cancelled = false;
    widgetsApi.list()
      .then(data => {
        if (!cancelled) setPage(data);
      })
      .catch(err => {
        console.error('Failed to load widgets:', err);
        if (!cancelled) setError('Could not load your widgets.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const updateWidget = (next) => {
    setPage(current => ({
      ...current,
      widgets: current.widgets.map(widget => (
        widget.workspace_id === next.workspace_id ? next : widget
      )),
    }));
  };

  const removeWidget = (workspaceId) => {
    setPage(current => ({
      ...current,
      widgets: current.widgets.filter(widget => widget.workspace_id !== workspaceId),
    }));
  };

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50/50 px-8 py-8 transition-colors duration-300 dark:bg-gray-950">
      <div className="mx-auto max-w-5xl space-y-5">
        <header>
          <h1 className="text-3xl font-black uppercase tracking-tight text-gray-900 dark:text-white">Widgets</h1>
          <p className="mt-2 max-w-2xl text-sm text-gray-500 dark:text-gray-400">
            Shared chats you can paste onto another site. Visitors ask questions without a Manteo login.
          </p>
        </header>

        {error && (
          <p className="rounded-2xl border border-red-100 bg-white px-4 py-3 text-sm text-red-600 dark:border-red-900/40 dark:bg-gray-900">
            {error}
          </p>
        )}

        {!page && !error && (
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading widgets…</p>
        )}

        {page && (
          <>
            <section className="grid gap-3 sm:grid-cols-2">
              <Stat
                label="Widget questions"
                value={formatNumber(page.widget_messages_this_period)}
              />
              <Stat
                label="Shared widgets"
                value={formatNumber(page.widgets.length)}
              />
            </section>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Widget questions are visitor messages from this billing period.
            </p>

            {page.widgets.length === 0 ? (
              <div className="rounded-3xl border border-dashed border-gray-200 bg-white px-6 py-10 text-center dark:border-gray-800 dark:bg-gray-900">
                <MessageSquare className="mx-auto text-emerald-600" size={22} />
                <h2 className="mt-3 text-base font-black text-gray-950 dark:text-white">No shared widgets yet</h2>
                <p className="mx-auto mt-1 max-w-md text-sm text-gray-500 dark:text-gray-400">
                  Open a workspace and publish its chat. The snippet will show up here, along with how many questions visitors ask.
                </p>
                <Link
                  to="/workspaces"
                  className="mt-4 inline-flex rounded-xl bg-emerald-600 px-4 py-2 text-xs font-black uppercase tracking-widest text-white hover:bg-emerald-700"
                >
                  Go to workspaces
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {page.widgets.map(widget => (
                  <WidgetCard
                    key={widget.workspace_id}
                    widget={widget}
                    onChange={updateWidget}
                    onRemove={removeWidget}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default WidgetsPage;
